import { Link } from 'react-router-dom'

/**
 * One product in the brand's product index.
 *
 * Everything shown is real: the name, the meta line (the product's own subtitle and
 * resolved tags), and the thumbnail where the product has an image. The card carried a
 * sample completeness dot and a `PKG` placeholder until the Veeva realignment; both are
 * gone, because a reader cannot tell an invented indicator from a measured one.
 */
export interface ProductCardProps {
	productId: string
	name: string
	/** Real meta — species/form, from the product's tags and subtitle. Empty when untagged. */
	meta: string
	/** The product's image, when one is classified to it. No placeholder stands in for its absence. */
	imageUrl?: string | null
	/** Geography code to carry into the product page, so it opens resolved for the same market. */
	market?: string | null
}

export function ProductCard({ productId, name, meta, imageUrl, market }: ProductCardProps) {
	return (
		<Link
			to={`/explore/product/${productId}${market ? `?market=${encodeURIComponent(market)}` : ''}`}
			className="flex flex-col gap-3 rounded-ds-card border border-ds-border bg-ds-surface-elevated p-5 transition-shadow hover:shadow-ds-md"
		>
			{/* A fixed ratio so a row of cards lines up whether or not each has an image. */}
			<span className="flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-ds-sm bg-ds-surface">
				{imageUrl ? (
					<img src={imageUrl} alt="" className="h-full w-full object-contain" loading="lazy" />
				) : (
					// The gap, stated rather than filled: no image is classified to this product.
					<span className="font-mono text-ds-text-subtle text-xs uppercase">No image</span>
				)}
			</span>
			<span className="min-w-0">
				<span className="block font-semibold text-ds-text leading-snug">{name}</span>
				{meta ? <span className="mt-1 block font-mono text-ds-2xs text-ds-text-muted">{meta}</span> : null}
			</span>
		</Link>
	)
}
